import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Gift } from 'lucide-react';
import { summaryApi } from 'common';
import { authFetch } from '../../helpers/authFetch';

function GiftCardPaymentVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(20);

  useEffect(() => {
    verifyPayment();
  }, []);

  useEffect(() => {
    if (status !== 'loading') {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [status, navigate]);

  const verifyPayment = async () => {
    const success = searchParams.get('success');
    const giftCardId = searchParams.get('giftCardId');
    const transactionId = searchParams.get('transaction_id') || searchParams.get('tx_ref');

    try {
      const response = await authFetch(summaryApi.verifyGiftCardPayment.url, {
                      method: summaryApi.verifyGiftCardPayment.method,
                      
                      headers: {
                          'Content-Type': 'application/json',
                      },
        body: JSON.stringify({
          giftCardId,
          success,
          transactionId
        })
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage('🎉 Gift card payment successful! The gift card has been sent to the recipient.');
      } else {
        setStatus('failed');
        setMessage('❌ Payment failed. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('failed');
      setMessage('⚠️ Unable to verify payment. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
          {/* Icon */}
          <div className="mb-6 flex justify-center">
            {status === 'loading' && (
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
            )}
            {status === 'failed' && (
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center animate-pulse">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-3xl font-black mb-4 text-gray-800">
            {status === 'loading' && 'Verifying Payment...'}
            {status === 'success' && 'Payment Successful!'}
            {status === 'failed' && 'Payment Failed'}
          </h1>

          {/* Message */}
          <p className="text-gray-600 mb-8 text-lg">
            {status === 'loading' ? 'Please wait while we confirm your gift card payment...' : message}
          </p>

          {/* Gift Card Icon */}
          {status === 'success' && (
            <div className="mb-8 flex justify-center">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 rounded-2xl">
                <Gift className="w-12 h-12 text-white" />
              </div>
            </div>
          )}

          {/* Countdown */}
          {status !== 'loading' && (
            <div className="bg-gray-100 rounded-xl p-6 mb-6">
              <p className="text-sm text-gray-600 mb-2">Redirecting to home in</p>
              <div className="text-4xl font-black text-gray-800">
                {countdown}s
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {status !== 'loading' && (
            <div className="space-y-3">
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:scale-105 transition-transform shadow-lg"
              >
                Go to Home Now
              </button>
              
              {status === 'success' && (
                <button
                  onClick={() => navigate('/my-giftcards')}
                  className="w-full bg-gray-100 text-gray-800 py-4 rounded-xl font-bold text-lg hover:bg-gray-200 transition-colors"
                >
                  View My Gift Cards
                </button>
              )}

              {status === 'failed' && (
                <button
                  onClick={() => navigate('/purchase-giftcard')}
                  className="w-full bg-gray-100 text-gray-800 py-4 rounded-xl font-bold text-lg hover:bg-gray-200 transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          )}

          {/* Loading Dots */}
          {status === 'loading' && (
            <div className="flex justify-center gap-2 mt-6">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-3 h-3 bg-blue-600 rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer Note */}
        <p className="text-center text-gray-500 text-sm mt-6">
          {status === 'success' && '✉️ A confirmation email has been sent to the recipient'}
          {status === 'failed' && '💬 Need help? Contact our support team'}
        </p>
      </div>
    </div>
  );
}

export default GiftCardPaymentVerify;